import { isRichTextValueConsideredEmpty } from "@uniformdev/richtext";

import { ButtonVariant } from "@/libraries/design/components/button/src/button";
import { LinkType } from "@/libraries/design/components/link/src/link";
import {
    ProductCardBlockLogoLockups,
    ProductCardBlockTitleAs,
    ProductCardBlockAlignment,
    ResponsiveProductCardBlockAlignment,
    ProductCardBlockCtaProps,
    ProductCardBlockColumns,
    ProductCardBlock
} from "@/libraries/design/components/product-card-block/src/product-card-block";
import { RichTextComponent } from "@/libraries/design/utils";
import { useLocalizedHref } from "@/libraries/web-framework";

export type UniformProductCardBlockProps = {
    title?: string;
    text?: string;
    uniformRichText?: unknown;
    richText?: string; // for rich text from Contentful
    logo?: keyof typeof ProductCardBlockLogoLockups;
    eyebrow?: string;
    titleAs?: ProductCardBlockTitleAs;
    buttonHref?: {
        path: string;
    };
    buttonLabel?: string;
    buttonVariant?: ButtonVariant;
    linkHref?: {
        path: string;
    };
    linkText?: string;
    linkType?: LinkType;
    alignment?: ProductCardBlockAlignment | ResponsiveProductCardBlockAlignment;
    imageUrl?: string;
    imageAlt?: string;
    spotIllustrationUrl?: string;
    iconUrl?: string;
    iconSize?: "x-small" | "small" | "medium" | "large";
    iconAlt?: string;
    orientation?: "vertical" | "horizontal" | "horizontal-reverse";
    columns?: "10" | "12";
    backgroundColor?: string;
};

export const UniformProductCardBlock = (
    props: UniformProductCardBlockProps
) => {
    const localizedLinkHref = useLocalizedHref(props.linkHref?.path ?? "");
    const localizedButtonHref = useLocalizedHref(props.buttonHref?.path ?? "");

    const callToActionProps: ProductCardBlockCtaProps | undefined =
        transformCtaProps({
            ...props,
            linkHref: props.linkHref ? { path: localizedLinkHref } : undefined,
            buttonHref: props.buttonHref
                ? { path: localizedButtonHref }
                : undefined
        });

    return (
        <ProductCardBlock
            title={props.title}
            text={
                props.richText ? (
                    <RichTextComponent text={props.richText} />
                ) : !isRichTextValueConsideredEmpty(props.uniformRichText) ? (
                    <RichTextComponent uniformParameter="uniformRichText" />
                ) : (
                    props.text
                )
            }
            titleAs={props.titleAs}
            logo={props.logo}
            eyebrow={props.eyebrow}
            alignment={props.alignment}
            callToActionProps={callToActionProps}
            pictureProps={
                props.imageUrl
                    ? {
                          image: {
                              src: props.imageUrl,
                              alt: props.imageAlt ?? ""
                          }
                      }
                    : undefined
            }
            spotIllustrationUrl={props.spotIllustrationUrl}
            icon={
                props.iconUrl
                    ? {
                          linkedIcon: {
                              url: props.iconUrl,
                              size: props.iconSize,
                              alt: props.iconAlt ?? ""
                          }
                      }
                    : undefined
            }
            orientation={props.orientation}
            columns={props.columns as unknown as ProductCardBlockColumns}
            backgroundColor={props.backgroundColor}
        />
    );
};

function transformCtaProps({
    buttonHref,
    buttonLabel,
    buttonVariant,
    linkHref,
    linkText,
    linkType
}: UniformProductCardBlockProps): ProductCardBlockCtaProps | undefined {
    const isButton = !!buttonHref?.path && !!buttonLabel;
    const isLink = !!linkHref?.path && !!linkText;

    if (isButton || isLink) {
        const ctaProps = {
            button: isButton
                ? {
                      href: buttonHref.path
                  }
                : undefined,
            buttonText: buttonLabel ?? undefined,
            buttonVariant: buttonVariant ?? undefined,
            link: isLink
                ? {
                      href: linkHref.path,
                      text: linkText,
                      type: linkType
                  }
                : undefined
        } as ProductCardBlockCtaProps;

        return ctaProps;
    }

    return undefined;
}

export default UniformProductCardBlock;
